import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { MODE_DARK, MODE_LIGHT } from 'constants/theme.constant'

const COLOR_STOPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900]

const normalizeLevel = (level) => {
    if (COLOR_STOPS.includes(level)) {
        return level
    }
    return 500
}

const shiftLevel = (level, delta) => {
    const normalized = normalizeLevel(level)
    const currentIdx = COLOR_STOPS.indexOf(normalized)
    const targetIdx = Math.min(
        Math.max(currentIdx + delta, 0),
        COLOR_STOPS.length - 1
    )
    return COLOR_STOPS[targetIdx]
}

const useThemePalette = () => {
    const theme = useSelector((state) => state.theme) || {}

    const {
        themeColor = 'orange',
        primaryColorLevel = 500,
        mode = MODE_LIGHT,
    } = theme

    return useMemo(() => {
        const baseLevel = normalizeLevel(primaryColorLevel)
        const softerLevel = shiftLevel(baseLevel, -2)
        const softestLevel = shiftLevel(baseLevel, -4)
        const liftLevel = shiftLevel(baseLevel, 1)
        const isDark = mode === MODE_DARK

        return {
            isDark,
            themeColor,
            primaryLevel: baseLevel,
            colorToken: `${themeColor}-${baseLevel}`,
            accent: {
                text: `text-${themeColor}-${baseLevel}`,
                border: `border-${themeColor}-${baseLevel}`,
                bg: `bg-${themeColor}-${baseLevel}`,
                bgHover: `hover:bg-${themeColor}-${liftLevel}`,
                softBg: `bg-${themeColor}-${softestLevel}`,
                softText: `text-${themeColor}-${liftLevel}`,
                pillBg: `bg-${themeColor}-${softerLevel}`,
                pillText: `text-${themeColor}-${liftLevel}`,
                focusRing: `focus:ring-${themeColor}-${baseLevel}`,
            },
            page: {
                bg: isDark ? 'bg-gray-950' : 'bg-gray-50',
                text: isDark ? 'text-gray-100' : 'text-gray-900',
                muted: isDark ? 'text-gray-400' : 'text-gray-500',
                border: isDark ? 'border-gray-800' : 'border-gray-200',
            },
            card: {
                bg: isDark ? 'bg-gray-900' : 'bg-white',
                text: isDark ? 'text-gray-100' : 'text-gray-900',
                border: isDark ? 'border-gray-800' : 'border-gray-200',
                shadow: isDark ? 'shadow-2xl' : 'shadow-sm',
            },
            panel: {
                bg: isDark ? 'bg-gray-900/80' : 'bg-white',
                border: isDark ? 'border-gray-800/80' : 'border-gray-200',
            },
        }
    }, [mode, primaryColorLevel, themeColor])
}

export default useThemePalette
